import { ContentCopy as ContentCopyIcon } from "@/components/icons";
import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  shareLinkUrl,
  useCreateShareLink,
  useDocumentShareLinks,
  useRevokeShareLink,
  type ShareLinkView,
} from "@/api/shareLinks";
import type { DocumentSummary } from "@/api/types";
import { useConfirm } from "@/hooks/useConfirm";
import { useOptionalToast } from "@/hooks/useToast";
import { formatDate, formatNumber } from "@/i18n/format";

/** "Share public link" (FW-35): make a link to THIS version of the
 *  document that anyone can open without signing in, copy it (and a
 *  ready-made share text) once, and see or revoke the document's active
 *  links. Whether the organisation allows it comes from the server. */
export function ShareLinkDialog({
  doc,
  open,
  onClose,
}: {
  doc: DocumentSummary;
  open: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const links = useDocumentShareLinks(doc.id, open);
  const create = useCreateShareLink(doc.id);
  const [expiry, setExpiry] = React.useState<string>("");
  const [created, setCreated] = React.useState<ShareLinkView | null>(null);

  const close = () => {
    setCreated(null);
    create.reset();
    onClose();
  };
  const data = links.data;
  const expiryValue = expiry || String(data?.default_expiry_days ?? 30);

  const submit = async () => {
    const days = expiryValue === "never" ? null : Number(expiryValue);
    try {
      setCreated(await create.mutateAsync(days));
    } catch {
      // The error shows below the form.
    }
  };

  let body: React.ReactNode;
  if (links.isLoading) {
    body = (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (links.isError || !data) {
    body = <Alert severity="error">{t("share_links.unavailable")}</Alert>;
  } else {
    body = (
      <Stack sx={{ gap: 2 }}>
        {created?.path ? (
          <CreatedLink title={doc.title} path={created.path} />
        ) : data.enabled ? (
          <>
            <Typography variant="body2">
              {t("share_links.intro", { version: doc.version })}
            </Typography>
            <Stack direction="row" sx={{ gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                select
                size="small"
                label={t("share_links.expiry_label")}
                value={expiryValue}
                onChange={(e) => setExpiry(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {data.expiry_days.map((days) => (
                  <MenuItem key={days ?? "never"} value={days === null ? "never" : String(days)}>
                    {days === null
                      ? t("share_links.expiry_never")
                      : t("share_links.expiry_days", { count: days })}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={create.isPending}
              >
                {t("share_links.create")}
              </Button>
            </Stack>
            {create.isError && (
              <Alert severity="error">{t("share_links.create_error")}</Alert>
            )}
          </>
        ) : (
          <Alert severity="info">{t("share_links.off")}</Alert>
        )}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("share_links.active_heading")}
          </Typography>
          <ShareLinksTable links={data.links} />
        </Box>
      </Stack>
    );
  }

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{t("share_links.dialog_title")}</DialogTitle>
      <DialogContent>{body}</DialogContent>
      <DialogActions>
        <Button onClick={close}>{t("share_links.done")}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** The new link, shown this once, with the share text. */
function CreatedLink({ title, path }: { title: string; path: string }): React.JSX.Element {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState<"link" | "text" | null>(null);
  const url = shareLinkUrl(path);
  const text = t("share_links.share_text", { title, url });
  const copy = async (what: "link" | "text") => {
    try {
      await navigator.clipboard.writeText(what === "link" ? url : text);
      setCopied(what);
    } catch {
      setCopied(null);
    }
  };
  return (
    <Stack sx={{ gap: 1.5 }}>
      <Alert severity="success">{t("share_links.shown_once")}</Alert>
      <TextField
        size="small"
        label={t("share_links.link_label")}
        value={url}
        slotProps={{ input: { readOnly: true } }}
        onFocus={(e) => e.target.select()}
      />
      <TextField
        size="small"
        multiline
        label={t("share_links.share_text_label")}
        value={text}
        slotProps={{ input: { readOnly: true } }}
        onFocus={(e) => e.target.select()}
      />
      <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={() => void copy("link")}
        >
          {copied === "link" ? t("share_links.copied") : t("share_links.copy_link")}
        </Button>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={() => void copy("text")}
        >
          {copied === "text" ? t("share_links.copied") : t("share_links.copy_text")}
        </Button>
      </Stack>
    </Stack>
  );
}

/** Active links with who made them, when, until when, views and Revoke.
 *  ``showDocument`` adds the document's title (the org admin's list). */
export function ShareLinksTable({
  links,
  showDocument = false,
}: {
  links: ShareLinkView[];
  showDocument?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useOptionalToast();
  const revoke = useRevokeShareLink();
  if (links.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("share_links.none")}
      </Typography>
    );
  }
  const requestRevoke = async (link: ShareLinkView) => {
    const ok = await confirm({
      title: t("share_links.revoke_confirm_title"),
      message: t("share_links.revoke_confirm_body"),
      confirmLabel: t("share_links.revoke"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await revoke.mutateAsync(link.id);
    } catch {
      toast?.(t("share_links.revoke_error"), "error");
    }
  };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {showDocument && <TableCell>{t("share_links.col_document")}</TableCell>}
            <TableCell>{t("share_links.col_version")}</TableCell>
            <TableCell>{t("share_links.col_created_by")}</TableCell>
            <TableCell>{t("share_links.col_created")}</TableCell>
            <TableCell>{t("share_links.col_expires")}</TableCell>
            <TableCell align="right">{t("share_links.col_views")}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((link) => (
            <TableRow key={link.id} data-testid="share-link-row">
              {showDocument && <TableCell>{link.document_title}</TableCell>}
              <TableCell>{t("share_links.version", { version: link.document_version })}</TableCell>
              <TableCell>{link.created_by_name ?? t("share_links.a_member")}</TableCell>
              <TableCell>{formatDate(link.created_at)}</TableCell>
              <TableCell>
                {link.expires_at ? formatDate(link.expires_at) : t("share_links.expiry_never")}
              </TableCell>
              <TableCell align="right">{formatNumber(link.view_count)}</TableCell>
              <TableCell align="right">
                {link.can_revoke && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => void requestRevoke(link)}
                    disabled={revoke.isPending}
                  >
                    {t("share_links.revoke")}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
